import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import {
    Button,
    Dropdown,
    message,
    Modal,
    Table,
    Tooltip
} from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CustomPagination from '../components/custom-pagination/CustomPagination';
import { apiService } from '../services/api';
import ConfigurationCreatePanel from '../components/configuration-create-panel';
import ConfigurationEditPanel from '../components/configuration-edit-panel';
import '../styles/sticky-columns.css';
import '../styles/configurations.css';

const PlusIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 5.5H6.5V0.5C6.5 0.367 6.447 0.24 6.354 0.146C6.26 0.053 6.133 0 6 0C5.867 0 5.74 0.053 5.646 0.146C5.553 0.24 5.5 0.367 5.5 0.5V5.5H0.5C0.367 5.5 0.24 5.553 0.146 5.646C0.053 5.74 0 5.867 0 6C0 6.133 0.053 6.26 0.146 6.354C0.24 6.447 0.367 6.5 0.5 6.5H5.5V11.5C5.5 11.633 5.553 11.76 5.646 11.854C5.74 11.947 5.867 12 6 12C6.133 12 6.26 11.947 6.354 11.854C6.447 11.76 6.5 11.633 6.5 11.5V6.5H11.5C11.633 6.5 11.76 6.447 11.854 6.354C11.947 6.26 12 6.133 12 6C12 5.867 11.947 5.74 11.854 5.646C11.76 5.553 11.633 5.5 11.5 5.5Z" fill="currentColor"/>
  </svg>
);

const getSpeedBadge = (slimiterKBytes) => {
  const val = Number(slimiterKBytes) || 0;

  if (!val || val <= 0) {
    return { label: 'Без ограничений', variant: 'blue' };
  }

  const mbps = Math.round((val * 8 / 1000) * 100) / 100;

  if (mbps < 5) {
    return { label: `${mbps} Мб/с`, variant: 'dark' };
  }

  if (mbps < 10) {
    return { label: `${mbps} Мб/с`, variant: 'grey' };
  }

  return { label: `${mbps} Мб/с`, variant: 'green' };
};

const Configurations = () => {
  const [configurations, setConfigurations] = useState([]);
  const [, setBaseTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState(null);
  const [editingConfiguration, setEditingConfiguration] = useState(null);

  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [compositions, setCompositions] = useState([]);
  const [, setEditCompositions] = useState([]);
  const [cfgPage, setCfgPage] = useState(1);
  const [cfgPerPage, setCfgPerPage] = useState(50);
  const cfgPagination = useMemo(() => ({
    current: cfgPage,
    pageSize: cfgPerPage,
    showSizeChanger: false,
    className: 'configurations-pagination-hidden',
  }), [cfgPage, cfgPerPage]);

  const memoizedConfigurations = useMemo(() => configurations, [configurations]);
  
  const handlePaginationChange = useCallback((paginationInfo) => {
    if (paginationInfo) {
      setCfgPage(paginationInfo.current);
      setCfgPerPage(paginationInfo.pageSize);
    }
  }, []);
  const fetchingRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (fetchingRef.current) {
      return;
    }
    
    try {
      fetchingRef.current = true;
      setLoading(true);
      const [configurationsRes, baseRes] = await Promise.all([
        apiService.getFlexibleTariffs(),
        apiService.getTariffPlans()
      ]);
      
      const newConfigurations = configurationsRes.data || [];
      const newBaseTariffs = baseRes.data || [];
      setConfigurations(newConfigurations);
      setBaseTariffs(newBaseTariffs);
    } catch (error) {
      let errorMessage = 'Ошибка загрузки данных';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => 
            typeof err === 'string' ? err : err.msg || 'Ошибка валидации'
          ).join(', ');
        } else if (typeof error.response.data.detail === 'object') {
          errorMessage = error.response.data.detail.msg || 'Ошибка валидации';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [createSubmitting, setCreateSubmitting] = useState(false);

  const handleCreateConfiguration = async (values) => {
    try {
      setCreateSubmitting(true);
      const configurationData = {
        max_proxies_per_port: 5,
        max_package_size: 10,
        default_quantities: [1, 3, 5, 10],
        base_price: 0.0,
        discount_per_quantity: 0.0,
        composition_discount: 0.0,
        is_template: false,
        requests_per_second_limit: 0,
        modem_settings_enabled: false,
        ...values,
        compositions: compositions,
        slimiter: values.slimiter > 0 ? Math.round(values.slimiter * 1000 / 8) : 0
      };
      await apiService.createFlexibleTariff(configurationData);
      message.success('Конфигурация успешно создана');
      setModalVisible(false);
      setCompositions([]);
      fetchData();
    } catch (error) {
      let errorMessage = 'Ошибка создания конфигурации';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => 
            typeof err === 'string' ? err : err.msg || 'Ошибка валидации'
          ).join(', ');
        } else if (typeof error.response.data.detail === 'object') {
          errorMessage = error.response.data.detail.msg || 'Ошибка валидации';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
    } finally {
      setCreateSubmitting(false);
    }
  };


  const handleUpdateConfiguration = async (values) => {
    try {
      const configurationData = {
        ...values,
        slimiter: (values.slimiter && values.slimiter > 0) ? Math.round(values.slimiter * 1000 / 8) : 0,
        maxconn: values.maxconn !== null && values.maxconn !== undefined ? values.maxconn : 0
      };
      await apiService.updateFlexibleTariff(editingConfiguration.id, configurationData);
      message.success('Конфигурация успешно обновлена');
      setEditModalVisible(false);
      setEditCompositions([]);
      setEditingConfiguration(null);
      fetchData();
    } catch (error) {
      let errorMessage = 'Ошибка обновления конфигурации';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => 
            typeof err === 'string' ? err : err.msg || 'Ошибка валидации'
          ).join(', ');
        } else if (typeof error.response.data.detail === 'object') {
          errorMessage = error.response.data.detail.msg || 'Ошибка валидации';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      message.error(errorMessage);
    }
  };

  const handleViewConfiguration = useCallback((record) => {
    setSelectedConfiguration(record);
    setViewModalVisible(true);
  }, []);

  const handleEditConfiguration = useCallback((record) => {
    setEditingConfiguration(record);
    setEditCompositions(record.compositions || []);
    setEditModalVisible(true);
  }, []);

  const handleDeleteConfiguration = useCallback((configuration) => {
    Modal.confirm({
      title: 'Подтверждение удаления',
      content: `Вы уверены, что хотите удалить конфигурацию "${configuration.name}"? Это действие нельзя отменить.`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await apiService.deleteFlexibleTariff(configuration.id);
          message.success(`Конфигурация "${configuration.name}" успешно удалена`);
          fetchData();
        } catch (error) {
          let errorMessage = 'Ошибка удаления конфигурации';
          
          if (error.response?.data?.detail) {
            if (typeof error.response.data.detail === 'string') {
              errorMessage = error.response.data.detail;
            } else if (Array.isArray(error.response.data.detail)) {
              errorMessage = error.response.data.detail.map(err => 
                typeof err === 'string' ? err : err.msg || 'Ошибка валидации'
              ).join(', ');
            } else if (typeof error.response.data.detail === 'object') {
              errorMessage = error.response.data.detail.msg || 'Ошибка валидации';
            }
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          message.error(errorMessage);
        }
      }
    });
  }, [fetchData]);

  const columns = useMemo(() => [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: isMobile ? 56 : 80,
      render: (id) => String(id).padStart(3, '0'),
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: isMobile ? 112 : undefined,
      render: (name, record) => (
        <span>
          {name}
          {record.sellable_in_cabinet === false && (
            <span className="configurations-speed-badge configurations-speed-badge--grey" style={{ marginLeft: 8 }}>
              Служебный
            </span>
          )}
          {record.is_active === false && (
            <span className="configurations-speed-badge configurations-speed-badge--dark" style={{ marginLeft: 8 }}>
              Выключен
            </span>
          )}
        </span>
      ),
    },
    {
      title: 'Прокси на порт',
      dataIndex: 'max_proxies_per_port',
      key: 'max_proxies_per_port',
      width: 160,
      sorter: (a, b) => (a.max_proxies_per_port || 0) - (b.max_proxies_per_port || 0),
      render: (val) => val ?? '—',
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description) => (
        <span className="configurations-cell--dark">
          {description || '—'}
        </span>
      ),
    },
    {
      title: 'Скорость',
      key: 'speed',
      width: 160,
      render: (_, record) => {
        const badge = getSpeedBadge(record.slimiter);
        return (
          <span className={`configurations-speed-badge configurations-speed-badge--${badge.variant}`}>
            {badge.label}
          </span>
        );
      },
    },
    {
      title: 'TCP соединения',
      key: 'tcp_connections',
      width: 160,
      render: (_, record) => {
        const maxConn = Number(record.maxconn) || 0;
        return maxConn;
      },
    },
    {
      title: 'Лимит запросов',
      key: 'rlimiter',
      width: 160,
      render: (_, record) => {
        const rlimiter = Number(record.rlimiter) || 0;
        return (
          <span className="configurations-cell--dark">
            {rlimiter} зап/с
          </span>
        );
      },
    },
    {
      title: 'Стоимость',
      key: 'base_price',
      width: 168,
      sorter: (a, b) => (Number(a.base_price) || 0) - (Number(b.base_price) || 0),
      render: (_, record) => {
        const fmt = (v) => (Math.round(Number(v) || 0)).toLocaleString('ru-RU');
        const d1 = Number(record.price_per_day) > 0 ? `${fmt(record.price_per_day)} ₽/д` : null;
        const d7 = Number(record.price_per_7_days) > 0 ? `${fmt(record.price_per_7_days)} ₽/7д` : null;
        const mo = `${fmt(record.base_price)} ₽/мес`;
        const parts = [d1, d7, mo].filter(Boolean);
        return (
          <span className="configurations-cell--dark configurations-cell--prices">
            {parts.join(' · ')}
          </span>
        );
      },
    },
    {
      title: 'Лимит клиентов',
      key: 'max_per_client',
      width: 160,
      render: (_, record) => (
        <span className="configurations-cell--dark">
          {record.max_per_client ?? '—'}
        </span>
      ),
    },
    {
      title: 'Лимит заказов',
      key: 'order_limits',
      width: 160,
      render: (_, record) => `${record.min_order_quantity || 1}-${record.max_order_quantity || 50}`,
    },
    {
      title: '',
      key: 'actions',
      width: 40,
      fixed: 'right',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'Просмотр',
            onClick: () => handleViewConfiguration(record)
          },
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Редактировать',
            onClick: () => handleEditConfiguration(record)
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Удалить',
            danger: true,
            onClick: () => handleDeleteConfiguration(record)
          }
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
            overlayClassName="pm-action-dropdown"
          >
            <button
              className="pm-action-dots"
              aria-label="Действия"
              tabIndex="0"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="pm-action-dots__dot" />
              <span className="pm-action-dots__dot" />
              <span className="pm-action-dots__dot" />
            </button>
          </Dropdown>
        );
      }
    },
  ], [isMobile, handleViewConfiguration, handleEditConfiguration, handleDeleteConfiguration]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader-container">
          <div className="loader-ball" />
          <div className="loader-shadow" />
        </div>
      </div>
    );
  }

  return (
    <div className="pm-content-fade-in">
      {/* Header по Figma */}
      <div className="configurations-header">
        <h1 className="configurations-header__title">Конфигурации</h1>
        <div className="configurations-header__spacer" />
        <div className="configurations-header__actions">
          <Tooltip title="Создать конфигурацию">
            <button
              type="button"
              className="configurations-header__action-btn configurations-header__action-btn--primary"
              onClick={() => setModalVisible(true)}
              aria-label="Создать конфигурацию"
              tabIndex="0"
            >
              <PlusIcon />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Таблица */}
      <div className="configurations-table">
        {Array.isArray(memoizedConfigurations) ? (
          <>
            <Table
              columns={columns}
              dataSource={memoizedConfigurations}
              rowKey="id"
              scroll={{ x: isMobile ? 1288 : 1400 }}
              size="middle"
              pagination={cfgPagination}
              onChange={handlePaginationChange}
            />
            <CustomPagination
              currentPage={cfgPage}
              totalPages={Math.max(1, Math.ceil((memoizedConfigurations?.length || 0) / cfgPerPage))}
              totalItems={memoizedConfigurations?.length || 0}
              perPage={cfgPerPage}
              perPageOptions={[5, 10, 20, 50]}
              onPageChange={(p) => setCfgPage(p)}
              onPerPageChange={(v) => { setCfgPerPage(v); setCfgPage(1); }}
            />
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div className="loader-container loader-container--sm">
              <div className="loader-ball" />
              <div className="loader-shadow" />
            </div>
            <div style={{ marginTop: '10px' }}>Загрузка конфигураций...</div>
          </div>
        )}
      </div>

      {/* Панель создания конфигурации */}
      <ConfigurationCreatePanel
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setCompositions([]);
        }}
        onSubmit={handleCreateConfiguration}
        submitting={createSubmitting}
      />

      {/* Модальное окно просмотра конфигурации */}
      <Modal
        className="light-modal"
        title="Информация о конфигурации"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Закрыть
          </Button>
        ]}
        width={isMobile ? '100%' : 520}
        style={isMobile ? { top: 16, maxWidth: 'calc(100vw - 32px)' } : undefined}
      >
        {selectedConfiguration && (
          <div className="cfg-info">
            <div className="cfg-info__name">{selectedConfiguration.name}</div>
            {selectedConfiguration.description && (
              <div className="cfg-info__desc">{selectedConfiguration.description}</div>
            )}

            <div className="cfg-info__section">
              <div className="cfg-info__section-title">Основные параметры</div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">Скорость</span>
                <span className="cfg-info__value">
                  {Number(selectedConfiguration.slimiter) > 0
                    ? <span className="cfg-info__badge cfg-info__badge--dark">{Math.round((Number(selectedConfiguration.slimiter) * 8 / 1000) * 100) / 100} Мб/с</span>
                    : <span className="cfg-info__badge cfg-info__badge--blue">Без ограничений</span>
                  }
                </span>
              </div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">Соединения</span>
                <span className="cfg-info__value">{selectedConfiguration.maxconn}</span>
              </div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">Лимит запросов</span>
                <span className="cfg-info__value">
                  {Number(selectedConfiguration.rlimiter) > 0
                    ? <span className="cfg-info__badge cfg-info__badge--grey">{Number(selectedConfiguration.rlimiter)} зап/с</span>
                    : <span className="cfg-info__badge cfg-info__badge--blue">Без ограничений</span>
                  }
                </span>
              </div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">
                  По 1 прокси на порт
                  <Tooltip title="Если включено, клиент не сможет получить несколько прокси на одном порту (предотвращает повторение IP)">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#5283f4', fontSize: 12 }} />
                  </Tooltip>
                </span>
                <span className="cfg-info__value">
                  {selectedConfiguration.one_proxy_per_port
                    ? <span className="cfg-info__badge cfg-info__badge--green">Да</span>
                    : <span className="cfg-info__badge cfg-info__badge--grey">Нет</span>
                  }
                </span>
              </div>
            </div>

            <div className="cfg-info__section">
              <div className="cfg-info__section-title">Ограничения</div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">Стоимость (1 день)</span>
                <span className="cfg-info__value">
                  {(Math.round(Number(selectedConfiguration.price_per_day) || 0)).toLocaleString('ru-RU')} ₽
                </span>
              </div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">Стоимость (7 дней)</span>
                <span className="cfg-info__value">
                  {(Math.round(Number(selectedConfiguration.price_per_7_days) || 0)).toLocaleString('ru-RU')} ₽
                </span>
              </div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">Стоимость (месяц)</span>
                <span className="cfg-info__value">{(Math.round(Number(selectedConfiguration.base_price) || 0)).toLocaleString('ru-RU')} ₽</span>
              </div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">На клиента</span>
                <span className="cfg-info__value">{selectedConfiguration.max_per_client}</span>
              </div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">Заказ</span>
                <span className="cfg-info__value">{selectedConfiguration.min_order_quantity}–{selectedConfiguration.max_order_quantity}</span>
              </div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">Срок действия</span>
                <span className="cfg-info__value">{selectedConfiguration.default_expires_days} дней</span>
              </div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">Автопродление</span>
                <span className="cfg-info__value">
                  {selectedConfiguration.auto_renewal
                    ? <span className="cfg-info__badge cfg-info__badge--green">Да</span>
                    : <span className="cfg-info__badge cfg-info__badge--grey">Нет</span>
                  }
                </span>
              </div>
              <div className="cfg-info__line">
                <span className="cfg-info__label">Настройки pOF</span>
                <span className="cfg-info__value">
                  {selectedConfiguration.modem_settings_enabled
                    ? <span className="cfg-info__badge cfg-info__badge--green">Да</span>
                    : <span className="cfg-info__badge cfg-info__badge--grey">Нет</span>
                  }
                </span>
              </div>
            </div>

            {selectedConfiguration.compositions && selectedConfiguration.compositions.length > 0 && (
              <div className="cfg-info__section">
                <div className="cfg-info__section-title">Композиция</div>
                <div className="cfg-info__tags">
                  {Array.isArray(selectedConfiguration.compositions) && selectedConfiguration.compositions.map((comp, index) => (
                    <span key={index} className="cfg-info__tag">
                      {comp.base_tariff_name} <span className="cfg-info__tag-qty">×{comp.quantity}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="cfg-info__footer-meta">
              Создан: {new Date(selectedConfiguration.created_at).toLocaleString('ru-RU')}
            </div>
          </div>
        )}
      </Modal>

      {/* Панель редактирования конфигурации */}
      <ConfigurationEditPanel
        visible={editModalVisible}
        configuration={editingConfiguration}
        onClose={() => {
          setEditModalVisible(false);
          setEditCompositions([]);
          setEditingConfiguration(null);
        }}
        onSubmit={handleUpdateConfiguration}
      />
    </div>
  );
};

export default Configurations;
